import * as React from 'react';
import { useSelector } from 'react-redux';

import { useApplicationContext } from 'shared/utils/applicationContext';
import { useAppDispatch } from 'shared/hooks/useAppDispatch';
import { useSendCPDAnalytics } from 'shared/hooks/useSendCPDAnalytics';
import { useIsDesktop } from 'shared/utils/useIsDesktop';
import { ECPDModalType } from 'shared/types/redux/cpd';
import { closeCPDModal, createCPDDraft, getCPDChatId } from 'shared/actions/cpd';
import { selectFirstAnyPhone, selectUserId } from 'shared/selectors/user';
import {
  selectCPDChatIdLoadingStatus,
  selectCPDDraftSource,
  selectCPDDraftStatus,
  selectCPDModalType,
} from 'shared/selectors/cpd';

import type { ICPDPrelandingConfig } from '../types';
import { useCallback, useState } from 'react';

export const useCPDProps = () => {
  const { config } = useApplicationContext();
  const dispatch = useAppDispatch();
  const cpdDraftStatus = useSelector(selectCPDDraftStatus);
  const cpdDraftSource = useSelector(selectCPDDraftSource);
  const userPhone = useSelector(selectFirstAnyPhone);
  const userId = useSelector(selectUserId);
  const cpdModalType = useSelector(selectCPDModalType);
  const cpdChatIdLoadingStatus = useSelector(selectCPDChatIdLoadingStatus);
  const isDesktop = useIsDesktop();
  const { sendCPDPrelandingModalEvent } = useSendCPDAnalytics();
  const [isMarketingAgreement, toggleMarketingAgreement] = useState<boolean>(false);

  const prelandingContent = config.get<ICPDPrelandingConfig>('CPDPrelandingData');

  const dataKey = (() => {
    if (cpdModalType === ECPDModalType.PrelandingHomeowner) {
      return 'homeowner' as const;
    }

    if (cpdModalType === ECPDModalType.PrelandingSearcher) {
      return 'searcher' as const;
    }

    return null;
  })();

  const prelandingContentData = dataKey ? prelandingContent?.[dataKey] : null;

  const [phoneValue, setPhoneValue] = React.useState(userPhone ?? null);

  const cpdLandingLink = React.useMemo(() => {
    const url = prelandingContentData?.secondaryBtn && prelandingContentData.secondaryBtn.url;

    if (url) {
      return cpdDraftSource ? `${url}?cian_source=${cpdDraftSource}_web` : url;
    }

    return null;
  }, [prelandingContentData, cpdDraftSource]);

  const onPhoneInputChange = React.useCallback((value: string) => {
    setPhoneValue(value);
  }, []);

  const onModalClose = React.useCallback(() => {
    dispatch(closeCPDModal());
    setPhoneValue(null);
  }, [dispatch]);

  const onModalOpen = React.useCallback(() => {
    sendCPDPrelandingModalEvent({ action: 'Open', label: 'Popup' });
  }, [sendCPDPrelandingModalEvent]);

  const onSuccessScreenOpen = React.useCallback(() => {
    sendCPDPrelandingModalEvent({ action: 'Send', label: 'Success' });
  }, [sendCPDPrelandingModalEvent]);

  const onCreateDraftCpd = React.useCallback(() => {
    sendCPDPrelandingModalEvent({ action: 'Click', label: 'Send' });
    const hasAgreementUrl = prelandingContentData && prelandingContentData.marketingAgreementUrl;
    const legalAgreementAccepted = hasAgreementUrl ? isMarketingAgreement : null;

    dispatch(createCPDDraft({ phone: phoneValue, legalAgreementAccepted }));
  }, [dispatch, sendCPDPrelandingModalEvent, phoneValue, isMarketingAgreement]);

  const onCPDLandingLinkClick = React.useCallback(() => {
    sendCPDPrelandingModalEvent({ action: 'Click', label: 'Detail' });
  }, [sendCPDPrelandingModalEvent]);

  const onGoCianChat = React.useCallback(async () => {
    sendCPDPrelandingModalEvent({ action: 'Click', label: 'ToChat' });

    const chatId = await dispatch(getCPDChatId());

    const url = chatId ? `https://www.cian.ru/dialogs/?chatId=${chatId}` : 'https://www.cian.ru/dialogs/?';

    window.open(url);
  }, [dispatch, sendCPDPrelandingModalEvent]);

  const handleChangeMarketingAgreement = useCallback(() => {
    toggleMarketingAgreement(!isMarketingAgreement);
  }, [isMarketingAgreement, toggleMarketingAgreement]);

  const isModalOpen =
    cpdModalType === ECPDModalType.PrelandingHomeowner || cpdModalType === ECPDModalType.PrelandingSearcher;

  const isUserAuthenticated = userId !== null;

  return React.useMemo(
    () => ({
      isDesktop,
      isModalOpen,
      cpdDraftStatus,
      phoneValue,
      onPhoneInputChange,
      onModalClose,
      onModalOpen,
      onCreateDraftCpd,
      onCPDLandingLinkClick,
      onGoCianChat,
      onSuccessScreenOpen,
      cpdChatIdLoadingStatus,
      prelandingContentData,
      cpdLandingLink,
      isUserAuthenticated,
      handleChangeMarketingAgreement,
      isMarketingAgreement,
    }),
    [
      isDesktop,
      isModalOpen,
      cpdDraftStatus,
      phoneValue,
      onPhoneInputChange,
      onModalClose,
      onModalOpen,
      onCreateDraftCpd,
      onCPDLandingLinkClick,
      onGoCianChat,
      onSuccessScreenOpen,
      cpdChatIdLoadingStatus,
      prelandingContentData,
      cpdLandingLink,
      isUserAuthenticated,
      handleChangeMarketingAgreement,
      isMarketingAgreement,
    ],
  );
};
